import { useSearchParams } from 'react-router-dom';
import { TimeWindowPicker, presetToDateRange } from '../components/TimeWindowPicker';
import { ServiceFilter } from '../components/ServiceFilter';
import { KPICard, KPIGrid } from '../components/KPICards';
import { SpendChart } from '../components/SpendChart';
import { CampaignTable } from '../components/CampaignTable';
import { useGoogleAdsData } from '../hooks/useGoogleAdsData';
import { formatBRL, formatPercent, formatNumber } from '../lib/format';
import type { ServiceLabel, TimeWindow } from '../types';

export default function GoogleAds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = (searchParams.get('preset') ?? '30d') as TimeWindow['preset'];
  const service = (searchParams.get('service') ?? 'all') as ServiceLabel;
  const { startDate, endDate } = presetToDateRange(preset);

  function setPreset(p: TimeWindow['preset']) { setSearchParams(sp => { sp.set('preset', p); return sp; }); }
  function setService(s: ServiceLabel) { setSearchParams(sp => { sp.set('service', s); return sp; }); }

  const { kpis, timeseries, campaigns, isLoading } = useGoogleAdsData({ startDate, endDate, service });

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--blue)', flexShrink: 0 }} className="pulse-dot" />
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>Google Ads</span>
          </div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Google Ads</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>Relatório de desempenho</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ServiceFilter value={service} onChange={setService} />
          <TimeWindowPicker value={preset} onChange={setPreset} />
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <KPIGrid columns={4}>
            <KPICard label="Valor Usado" value={formatBRL(kpis?.valorUsado ?? 0)} accentColor="var(--blue)" delay={0} />
            <KPICard label="Impressões" value={formatNumber(kpis?.impressions ?? 0)} delay={0.04} />
            <KPICard label="Cliques" value={formatNumber(kpis?.clicks ?? 0)} accentColor="var(--green)" delay={0.08} />
            <KPICard label="CTR" value={formatPercent(kpis?.ctr ?? 0)} delay={0.12} />
            <KPICard label="CPM" value={formatBRL(kpis?.cpm ?? 0)} delay={0.16} />
            <KPICard label="Conversões" value={formatNumber(kpis?.conversions ?? 0)} accentColor="var(--green)" delay={0.20} />
            <KPICard label="Custo/Conversão" value={formatBRL(kpis?.costPerConversion ?? 0)} accentColor="var(--red)" delay={0.24} />
          </KPIGrid>

          <div style={{ marginTop: '24px' }}>
            <SpendChart data={timeseries} showMeta={false} showGoogle height={200} />
          </div>

          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Campanhas
            </h2>
            <CampaignTable campaigns={campaigns} />
          </div>
        </>
      )}
    </div>
  );
}
